// The 15 seats as the v10 UI needs them: who sits where, what they're the
// ability to do, which shell tool they drive, and their v10 portrait.
// Names/ids follow the app canon (ascension.ts GEAR_TIERS, spectrum-tools.ts),
// ability lines follow docs/redesign/CANON_FOR_UI.md §1.
import type { ImageSourcePropType } from 'react-native';
import { GEAR_TIERS, TIER_ORDER, type GearId, type TierId } from '../olmps/ascension';
import type { DmtToolId } from '../olmps/spectrum-tools';
import { DMT_ART } from '../olmps/deityArt';

export const GEAR_ORDER: GearId[] = ['orient', 'regulate', 'direct', 'execute', 'revise'];

export const GEAR_LABEL: Record<GearId, { letter: string; name: string; question: string; tag: string }> = {
  orient: { letter: 'O', name: 'Orient', question: 'Where am I going?', tag: 'See clearly' },
  regulate: { letter: 'R', name: 'Regulate', question: 'What do I need?', tag: 'Balance & align' },
  direct: { letter: 'D', name: 'Direct', question: 'How will I get there?', tag: 'Choose & commit' },
  execute: { letter: 'E', name: 'Execute', question: 'Do the thing.', tag: 'Take action' },
  revise: { letter: 'R', name: 'Revise', question: 'What changed?', tag: 'Learn & evolve' },
};

export interface Seat {
  id: DmtToolId;
  name: string;
  gear: GearId;
  tier: TierId;
  ability: string;
  /** Tool id the shell's openTool() understands (nav quirks: astra→astrs, janus→jnus). */
  shellTool: string;
}

const ABILITY: Record<DmtToolId, string> = {
  telos: 'set an Endeavour and steer by it',
  oracle: 'take honest counsel on where you stand',
  astra: 'hold the whole board in one view',
  moyri: 'rewrite the patterns that run you',
  numa: 'match load to the energy you have',
  hrmez: 'hold signal through noise',
  kyrs: 'schedule by priority',
  krnos: 'give intention a place in time',
  tkne: 'turn a large intention into stages',
  metis: 'give today an order',
  themis: 'break action into a protocol',
  janus: 'decide by criteria and stay decided',
  clio: 'close the loop and carry the lesson',
  argos: 'see what actually happened',
  nemos: 'file and retrieve without loss',
};

const SHELL_TOOL: Partial<Record<DmtToolId, string>> = { astra: 'astrs', janus: 'jnus' };

export const SEATS: Seat[] = GEAR_ORDER.flatMap((gear) =>
  TIER_ORDER.map((tier) => {
    const id = GEAR_TIERS[gear][tier];
    return { id, name: id.toUpperCase(), gear, tier, ability: ABILITY[id], shellTool: SHELL_TOOL[id] ?? id };
  }),
);

export const SEAT_BY_ID: Record<string, Seat> = Object.fromEntries(SEATS.map((s) => [s.id, s]));
// Shell-tool aliases resolve too, so a KYRS/JNUS tool id finds its seat.
SEAT_BY_ID.jnus = SEAT_BY_ID.janus;
SEAT_BY_ID.astrs = SEAT_BY_ID.astra;

/** v10 white-and-gold portraits (docs/redesign/ART_LOG.md) — all 15 seats.
 *  Fallback to the older chiaroscuro art stays so the UI never shows a hole. */
const V10_PORTRAIT: Partial<Record<DmtToolId, ImageSourcePropType>> = {
  telos: require('../../assets/v10/deities/telos.jpg'),
  oracle: require('../../assets/v10/deities/oracle.jpg'),
  astra: require('../../assets/v10/deities/astra.jpg'),
  moyri: require('../../assets/v10/deities/moyri.jpg'),
  numa: require('../../assets/v10/deities/numa.jpg'),
  hrmez: require('../../assets/v10/deities/hrmez.jpg'),
  kyrs: require('../../assets/v10/deities/kyrs.jpg'),
  krnos: require('../../assets/v10/deities/krnos.jpg'),
  tkne: require('../../assets/v10/deities/tkne.jpg'),
  themis: require('../../assets/v10/deities/themis.jpg'),
  argos: require('../../assets/v10/deities/argos.jpg'),
  nemos: require('../../assets/v10/deities/nemos.jpg'),
  metis: require('../../assets/v10/deities/metis.jpg'),
  janus: require('../../assets/v10/deities/janus.jpg'),
  clio: require('../../assets/v10/deities/clio.jpg'),
};

/** Background-removed cutouts (rembg isnet, art-masters/v10/deities/cutouts) for composed scenes. */
const V10_CUTOUT: Record<DmtToolId, ImageSourcePropType> = {
  telos: require('../../assets/v10/deities/cut/telos.png'),
  oracle: require('../../assets/v10/deities/cut/oracle.png'),
  astra: require('../../assets/v10/deities/cut/astra.png'),
  moyri: require('../../assets/v10/deities/cut/moyri.png'),
  numa: require('../../assets/v10/deities/cut/numa.png'),
  hrmez: require('../../assets/v10/deities/cut/hrmez.png'),
  kyrs: require('../../assets/v10/deities/cut/kyrs.png'),
  krnos: require('../../assets/v10/deities/cut/krnos.png'),
  tkne: require('../../assets/v10/deities/cut/tkne.png'),
  metis: require('../../assets/v10/deities/cut/metis.png'),
  themis: require('../../assets/v10/deities/cut/themis.png'),
  janus: require('../../assets/v10/deities/cut/janus.png'),
  clio: require('../../assets/v10/deities/cut/clio.png'),
  argos: require('../../assets/v10/deities/cut/argos.png'),
  nemos: require('../../assets/v10/deities/cut/nemos.png'),
};

export function cutoutFor(id: string): ImageSourcePropType | undefined {
  const seat = SEAT_BY_ID[id];
  return seat ? V10_CUTOUT[seat.id] : undefined;
}

export function portraitFor(id: string): ImageSourcePropType | undefined {
  const seat = SEAT_BY_ID[id];
  if (!seat) return undefined;
  return V10_PORTRAIT[seat.id] ?? DMT_ART[seat.id] ?? DMT_ART[seat.shellTool];
}

export const MOUNTAIN_TILT = require('../../assets/v10/mountain/tilt.jpg');
export const MOUNTAIN_AERIAL = require('../../assets/v10/mountain/aerial.jpg');
export const TEMPLE_HALL = require('../../assets/v10/scenes/hall.jpg');
